import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  DollarSign, BedDouble, Calendar, MapPin, Sparkles, User as UserIcon,
  ChevronLeft, ChevronRight, Check, Loader2, Phone, MessageCircle,
  ShieldCheck, Tag, Clock
} from "lucide-react";
import { API } from "../App";
import { track } from "../lib/analytics";
import { getAttribution } from "../lib/attribution";

const STORAGE_KEY = "unq_quiz_state_v1";
// Use sessionStorage so the draft is auto-cleared when the user closes the tab
// (more appropriate than localStorage for short-lived form state).
const draftStore = typeof window !== "undefined" ? window.sessionStorage : null;

const STEPS = [
  {
    key: "budget",
    icon: DollarSign,
    title: "What's your monthly budget?",
    subtitle: "We'll match you to deals in your sweet spot.",
    type: "single",
    options: [
      { label: "Under $1,200", value: "under_1200", min: 0,    max: 1200 },
      { label: "$1,200 – $1,500", value: "1200_1500", min: 1200, max: 1500 },
      { label: "$1,500 – $2,000", value: "1500_2000", min: 1500, max: 2000 },
      { label: "$2,000+",          value: "2000_plus", min: 2000, max: 5000 },
    ],
  },
  {
    key: "bedrooms",
    icon: BedDouble,
    title: "How much space do you need?",
    subtitle: "Pick the layout you're after.",
    type: "single",
    options: [
      { label: "Studio",       value: "Studio" },
      { label: "1 Bedroom",    value: "1 Bed" },
      { label: "2 Bedrooms",   value: "2 Bed" },
      { label: "3+ Bedrooms",  value: "3+ Bed" },
    ],
  },
  {
    key: "moveIn",
    icon: Calendar,
    title: "When are you planning to move?",
    subtitle: "Timing helps us hunt the right specials.",
    type: "single",
    options: [
      { label: "ASAP",            value: "asap" },
      { label: "Within 30 days",  value: "30_days" },
      { label: "1–2 months",      value: "1_2_months" },
      { label: "Just browsing",   value: "browsing" },
    ],
  },
  {
    key: "area",
    icon: MapPin,
    title: "Which area are you most interested in?",
    subtitle: "We cover the whole Houston metro.",
    type: "single",
    options: [
      { label: "Houston (Inner Loop)", value: "Houston" },
      { label: "Katy",                  value: "Katy" },
      { label: "The Woodlands",         value: "The Woodlands" },
      { label: "Sugar Land",            value: "Sugar Land" },
      { label: "Open to suggestions",   value: "open" },
    ],
  },
  {
    key: "lifestyle",
    icon: Sparkles,
    title: "What matters most to you?",
    subtitle: "Pick all that apply — we tune your matches around these.",
    type: "multi",
    options: [
      { label: "Luxury finishes",       value: "luxury" },
      { label: "Lowest price",          value: "value" },
      { label: "Close to work",         value: "commute" },
      { label: "Nightlife / walkability", value: "nightlife" },
      { label: "Family-friendly",       value: "family" },
      { label: "Pet-friendly",          value: "pet" },
    ],
  },
  {
    key: "contact",
    icon: UserIcon,
    title: "🎉 Your matches are ready!",
    subtitle: "Where should we send your personalized list?",
    type: "form",
  },
];

const TOTAL = STEPS.length;

const QuizPage = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    budget: null, bedrooms: null, moveIn: null, area: null,
    lifestyle: [], name: "", phone: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // Hydrate from sessionStorage
  useEffect(() => {
    try {
      const saved = JSON.parse(draftStore?.getItem(STORAGE_KEY) || "null");
      if (saved && saved.answers) {
        setAnswers(saved.answers);
        if (typeof saved.step === "number" && saved.step < TOTAL) setStep(saved.step);
      }
    } catch {}
    // STORAGE_KEY / TOTAL / draftStore are module-level constants — mount-only run is intentional
  }, []);

  // Persist
  useEffect(() => {
    if (done) return;
    draftStore?.setItem(STORAGE_KEY, JSON.stringify({ step, answers }));
    // STORAGE_KEY / draftStore are module-level constants
  }, [step, answers, done]);

  // Fire quiz_started analytics once on first mount
  useEffect(() => {
    track("quiz_started", { source: "quiz" });
  }, []);

  const current = STEPS[step];
  const progress = ((step + 1) / TOTAL) * 100;

  const isStepComplete = useMemo(() => {
    if (current.type === "single") return !!answers[current.key];
    if (current.type === "multi") return (answers[current.key] || []).length > 0;
    if (current.type === "form")
      return answers.name.trim() && answers.phone.trim() && /\S+@\S+\.\S+/.test(answers.email);
    return false;
  }, [current, answers]);

  const select = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));
  const toggleMulti = (key, val) =>
    setAnswers((a) => {
      const arr = a[key] || [];
      return {
        ...a,
        [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val],
      };
    });

  const next = () => {
    if (!isStepComplete) return;
    if (step < TOTAL - 1) setStep(step + 1);
  };
  const back = () => step > 0 && setStep(step - 1);

  const submit = async () => {
    if (!isStepComplete) return;
    setSubmitting(true);
    setErrMsg("");
    try {
      const budgetOpt = STEPS[0].options.find((o) => o.value === answers.budget);
      const areaVal = answers.area === "open" ? null : answers.area;
      const payload = {
        name: answers.name.trim(),
        email: answers.email.trim(),
        phone: answers.phone.trim(),
        lead_type: "renter",
        source: "quiz",
        budget_min: budgetOpt?.min ?? null,
        budget_max: budgetOpt?.max ?? null,
        bedrooms: answers.bedrooms,
        preferred_areas: areaVal ? [areaVal] : [],
        move_in_date: answers.moveIn,
        lifestyle_priorities: answers.lifestyle || [],
        message: `Quiz lead — Move: ${answers.moveIn}; Lifestyle: ${(answers.lifestyle || []).join(", ") || "—"}`,
        ...getAttribution(),
      };
      await axios.post(`${API}/leads`, payload);
      draftStore?.removeItem(STORAGE_KEY);
      track("quiz_completed", { source: "quiz" });
      track("lead_created", { source: "quiz" });
      setDone(true);
    } catch (e) {
      setErrMsg(
        e?.response?.data?.detail
          ? typeof e.response.data.detail === "string" ? e.response.data.detail : "Submission failed."
          : "Network error. Please try again."
      );
    }
    setSubmitting(false);
  };

  if (done) return <SuccessScreen />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2342] via-[#0d2d54] to-[#102a4f] py-10 px-4 md:py-16">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="text-center text-white mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] leading-tight"
            data-testid="quiz-title"
          >
            Find Your Perfect Houston<br className="hidden sm:block" /> Apartment in 60 Seconds
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-sm sm:text-base text-white/70 mt-3 max-w-xl mx-auto"
          >
            Answer a few quick questions and get a personalized list with hidden specials, pricing, and availability.
          </motion.p>
        </div>

        {/* Card */}
        <motion.div
          layout
          className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="quiz-card"
        >
          {/* Progress */}
          <div className="px-6 pt-6">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-400 mb-2">
              <span data-testid="quiz-step-label">Step {step + 1} of {TOTAL}</span>
              <span className="text-[#DE6E4B]">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#DE6E4B] to-[#f08964] rounded-full"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                data-testid="quiz-progress-bar"
              />
            </div>
          </div>

          {/* Step content */}
          <div className="p-6 sm:p-8 min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                data-testid={`quiz-step-${step}`}
              >
                <div className="flex items-center gap-2 text-[#DE6E4B] mb-2">
                  <current.icon className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {current.type === "form" ? "Last step" : `Question ${step + 1}`}
                  </span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="quiz-question">
                  {current.title}
                </h2>
                <p className="text-sm text-slate-500 mt-1.5">{current.subtitle}</p>

                {/* Options */}
                <div className="mt-6 space-y-3">
                  {current.type === "single" &&
                    current.options.map((o) => (
                      <OptionButton
                        key={o.value}
                        label={o.label}
                        selected={answers[current.key] === o.value}
                        onClick={() => {
                          select(current.key, o.value);
                          // Auto-advance after a beat for snappy UX
                          setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL - 1)), 220);
                        }}
                        testId={`quiz-opt-${current.key}-${o.value}`}
                      />
                    ))}

                  {current.type === "multi" && (
                    <>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {current.options.map((o) => (
                          <OptionButton
                            key={o.value}
                            label={o.label}
                            selected={(answers[current.key] || []).includes(o.value)}
                            onClick={() => toggleMulti(current.key, o.value)}
                            multi
                            testId={`quiz-opt-${current.key}-${o.value}`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 pt-2 italic">
                        Select all that apply
                      </p>
                    </>
                  )}

                  {current.type === "form" && (
                    <ContactForm
                      answers={answers}
                      setAnswers={setAnswers}
                      submit={submit}
                      submitting={submitting}
                      errMsg={errMsg}
                      isComplete={isStepComplete}
                    />
                  )}
                </div>

                {/* Social proof — only on early steps */}
                {step <= 2 && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                    className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    People like you found deals up to <strong>8 weeks free</strong>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer Nav */}
          {current.type !== "form" && (
            <div className="px-6 pb-6 sm:px-8 sm:pb-8 flex items-center justify-between border-t border-slate-100 pt-4">
              <button
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="quiz-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={next}
                disabled={!isStepComplete}
                className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#0A2342] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2d54] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="quiz-next-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>

        {/* Trust strip */}
        <div className="mt-6 grid grid-cols-3 gap-2 text-center">
          <TrustBadge icon={ShieldCheck} text="100% Free Service" />
          <TrustBadge icon={DollarSign}  text="You pay nothing" />
          <TrustBadge icon={Sparkles}    text="Unlisted specials" />
        </div>
      </div>
    </div>
  );
};

const OptionButton = ({ label, selected, onClick, multi, testId }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all flex items-center justify-between gap-3 ${
      selected
        ? "border-[#DE6E4B] bg-[#DE6E4B]/5 text-[#0A2342]"
        : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
    }`}
    data-testid={testId}
  >
    <span className="text-[15px] font-medium">{label}</span>
    <span
      className={`flex items-center justify-center w-5 h-5 rounded-${multi ? "md" : "full"} border-2 transition-all ${
        selected ? "bg-[#DE6E4B] border-[#DE6E4B]" : "border-slate-300"
      }`}
    >
      {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </span>
  </motion.button>
);

const ContactForm = ({ answers, setAnswers, submit, submitting, errMsg, isComplete }) => (
  <div className="space-y-3">
    <input
      type="text"
      placeholder="Full Name"
      value={answers.name}
      onChange={(e) => setAnswers((a) => ({ ...a, name: e.target.value }))}
      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#DE6E4B] focus:outline-none text-[15px]"
      data-testid="quiz-input-name"
    />
    <input
      type="tel"
      placeholder="Phone Number"
      value={answers.phone}
      onChange={(e) => setAnswers((a) => ({ ...a, phone: e.target.value }))}
      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#DE6E4B] focus:outline-none text-[15px]"
      data-testid="quiz-input-phone"
    />
    <input
      type="email"
      placeholder="Email Address"
      value={answers.email}
      onChange={(e) => setAnswers((a) => ({ ...a, email: e.target.value }))}
      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#DE6E4B] focus:outline-none text-[15px]"
      data-testid="quiz-input-email"
    />

    {errMsg && (
      <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" data-testid="quiz-error">
        {errMsg}
      </div>
    )}

    <button
      type="button"
      onClick={submit}
      disabled={!isComplete || submitting}
      className="w-full mt-2 py-3.5 bg-[#DE6E4B] text-white rounded-xl text-base font-bold hover:bg-[#c85e3d] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      data-testid="quiz-submit-btn"
    >
      {submitting ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" /> Sending…
        </>
      ) : (
        <>Get My Apartment List <ChevronRight className="w-5 h-5" /></>
      )}
    </button>

    <p className="text-[11px] text-slate-400 text-center pt-1">
      By submitting, you agree to receive helpful texts/emails about your search. Unsub anytime.
    </p>
  </div>
);

const TrustBadge = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center justify-center gap-1.5 text-[11px] sm:text-xs text-white/70 px-2 py-1.5 rounded-lg bg-white/5 backdrop-blur-sm">
    <Icon className="w-3.5 h-3.5" />
    <span>{text}</span>
  </div>
);

const SuccessScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-[#0A2342] via-[#0d2d54] to-[#102a4f] flex items-center justify-center px-4 py-14">
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 sm:p-10 text-center"
      data-testid="quiz-success"
    >
      <motion.div
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
        className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5"
      >
        <Check className="w-8 h-8 text-emerald-600" strokeWidth={3} />
      </motion.div>
      <h1 className="text-3xl sm:text-4xl font-bold text-[#0A2342] font-['Playfair_Display']">
        You're In! 🎯
      </h1>
      <p className="text-slate-600 mt-3 leading-relaxed">
        Your custom apartment matches are being prepared now. A locator will reach out shortly with the best options and specials.
      </p>

      <div className="mt-6 p-4 bg-[#0A2342]/5 border border-[#0A2342]/10 rounded-xl">
        <p className="text-sm font-medium text-[#0A2342]">Want faster results?</p>
        <a
          href="sms:9362241484"
          className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-[#DE6E4B] text-white rounded-lg text-sm font-bold hover:bg-[#c85e3d]"
          data-testid="quiz-success-text-btn"
        >
          <MessageCircle className="w-4 h-4" /> Text us at (936) 224-1484
        </a>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Avg response: under 30 min</span>
        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Or call (936) 224-1484</span>
      </div>

      <Link
        to="/"
        className="block mt-8 text-sm text-slate-500 hover:text-[#0A2342] underline"
        data-testid="quiz-success-home-link"
      >
        ← Back to home
      </Link>
    </motion.div>
  </div>
);

export default QuizPage;
